import { createFeatureSelector, createSelector, select } from '@ngrx/store';
import { combineLatest, map, Observable, of } from 'rxjs';

import { ErrorScannedCode, ScannedCodeType } from 'app/features/scan-flow/models';
import { selectScanFlowActivity } from 'app/features/scan-flow/state/scan-flow/scan-flow.selectors';
import { selectBatchByNumber } from 'app/shared/features/batch-selector/state/batch.selectors';
import { selectActivityByNumber } from 'app/state/activities/activity.selectors';
import { selectContainerByNumber } from 'app/state/containers/container.selectors';
import { selectEmployeeByNumber } from 'app/state/employee/employee.selectors';
import { selectLocationByNumber } from 'app/state/locations/locations.selectors';

import { scannedCodeTitle } from '../scanned-codes/utils/scanned-code-title.utils';
import {
    getScanErrorActivity,
    getScanErrorEmployee,
    getScanErrorLocation,
    getShortScannedCode,
    parseScannedCode,
} from '../scanned-codes/utils/scanned-code.utils';
import { scannedCodesFeatureKey, ScannedCodeState } from './scanned-codes.reducer';

export const selectScannedCodesState = createFeatureSelector<ScannedCodeState>(scannedCodesFeatureKey);

export const selectScannedCodes = createSelector(selectScannedCodesState, (state) => state.scannedCodes);

export const selectScannedCodeByToken = (token: string) =>
    createSelector(selectScannedCodes, (state) => state[token]);

export const selectIsScannedCodeValid =
    ({ scannedCode, allowedCodeTypes }: { scannedCode: string; allowedCodeTypes: ScannedCodeType[] }) =>
    (store: Observable<object>): Observable<{ error: ErrorScannedCode | undefined }> => {
        if (!allowedCodeTypes.length) return of({ error: undefined });

        const parsedCode = parseScannedCode({ allowedCodeTypes, code: scannedCode });
        const { codeType, number, error } = parsedCode;
        if (!number || error) return of({ error });

        switch (codeType) {
            case 'employee':
                return store.pipe(
                    selectEmployeeByNumber(number),
                    map((employee) => getScanErrorEmployee(employee, codeType, scannedCode)),
                );
            case 'location':
                return combineLatest([
                    store.pipe(selectLocationByNumber(number)),
                    store.pipe(selectScanFlowActivity),
                ]).pipe(
                    map(([location, currentActivity]) =>
                        getScanErrorLocation(location, currentActivity, codeType, scannedCode),
                    ),
                );
            case 'container':
                return store.pipe(
                    select(selectContainerByNumber(number)),
                    map((container) => ({
                        error: {
                            message: !container ? 'SCAN.ERROR.TITLE.UNKNOWN_CODE_SCANNED' : undefined,
                            scannedCodeType: scannedCodeTitle[codeType],
                            shortScannedValue: getShortScannedCode(scannedCode),
                        },
                    })),
                );
            case 'activity': {
                return store.pipe(
                    selectActivityByNumber(number),
                    map((activity) => getScanErrorActivity(activity, codeType, scannedCode)),
                );
            }
            case 'batch': {
                return store.pipe(
                    selectBatchByNumber(number),
                    map((batch) => ({
                        error: {
                            message: !batch ? 'SCAN.ERROR.TITLE.UNKNOWN_CODE_SCANNED' : undefined,
                            scannedCodeType: scannedCodeTitle[codeType],
                            shortScannedValue: getShortScannedCode(scannedCode),
                        },
                    })),
                );
            }
            default:
                return of({ error: undefined });
        }
    };
